import { invokeApi } from '../bl_libs/invokeApi';
import { LOCAL_STORAGE_KEYS } from '../utils/constants';

/**
 * CPC Index Government Dashboard — API client.
 *
 * Namespace: /v1/cpc_gov (mounted separately from the Insider and Seal portals
 * because the audience and the auth story are different).
 *
 * SCOPING RULE (approved flow, slide 18 step 11): no call here sends a country.
 * A Gov account is fixed to one country_code and the server resolves it from the
 * session. If you find yourself wanting to pass a country, that is the bug.
 */

/* -----------------------------------------------------------------------
 * Session
 *
 * The Gov portal keeps its own tokens so signing into it never disturbs an
 * Insider or Seal session in the same browser.
 * --------------------------------------------------------------------- */

const readJson = (key) => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

export const getCpcGovToken = () => readJson(LOCAL_STORAGE_KEYS.cpc_gov_auth_token);
export const getCpcGovUser = () => readJson(LOCAL_STORAGE_KEYS.cpc_gov_auth_user);
export const isCpcGovLoggedIn = () => {
    const token = getCpcGovToken();
    if (!token) return false;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && payload.exp * 1000 <= Date.now()) {
            return false;
        }
    } catch (e) {}
    return true;
};
export const setCpcGovSession = ({ auth_token, refresh_token, user }) => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.cpc_gov_auth_token, JSON.stringify(auth_token));
    localStorage.setItem(LOCAL_STORAGE_KEYS.cpc_gov_refresh_token, JSON.stringify(refresh_token));
    localStorage.setItem(LOCAL_STORAGE_KEYS.cpc_gov_auth_user, JSON.stringify(user));
};

export const clearCpcGovSession = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.cpc_gov_auth_token);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.cpc_gov_refresh_token);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.cpc_gov_auth_user);
};

const authHeaders = () => ({ Authorization: `Bearer ${getCpcGovToken()}` });

/* -----------------------------------------------------------------------
 * Reference data (public)
 * --------------------------------------------------------------------- */

/**
 * The 100 CPCI countries — UN M49 official names + ISO alpha-2 codes.
 *
 * Public (no token): the list is reference data and is needed before any Gov
 * account exists. Served from the CPC-owned `cpci_country_list` collection.
 */
export const getCpciCountriesService = () => {
    const requestObj = {
        path: '/cpc_gov/meta/countries',
        method: 'GET',
        headers: {},
    };
    return invokeApi(requestObj);
};

/**
 * External display names for the dashboard's hierarchical codes (S1, T05,
 * FC-05 ...), from the CPC-owned `external_code_map` collection.
 *
 * Fetch this ONCE when the dashboard loads and keep it in context — that is
 * exactly what the ML side asked for (Seong Min, 2026-08-04). It lives in the
 * database rather than in frontend constants so a wording change requested by
 * Dr. Park does not need a frontend redeploy.
 *
 * Returns { code_map, codes, total, types, source } where
 * code_map[type][code] = { name, parent_code }.
 */
export const getCpcGovCodeMapService = () => {
    const requestObj = {
        path: '/cpc_gov/meta/code_map',
        method: 'GET',
        headers: {},
    };
    return invokeApi(requestObj);
};

/* -----------------------------------------------------------------------
 * Auth
 * --------------------------------------------------------------------- */

/**
 * Sign a government reviewer in.
 *
 * Accounts are pre-created — one per CPCI country — so there is no register
 * call. The response's user carries country_code, which the dashboard displays
 * but never sends back.
 */
export const cpcGovLoginService = async (email, password) => {
    const requestObj = {
        path: '/cpc_gov/auth/cpcgov/login',
        method: 'POST',
        headers: {},
        postData: { email, password },
    };
    const result = await invokeApi(requestObj);
    if (result && result.data && result.data.auth_token) {
        setCpcGovSession(result.data);
    }
    return result;
};

export const cpcGovLogoutService = async () => {
    try {
        await invokeApi({
            path: '/cpc_gov/auth/cpcgov/logout',
            method: 'POST',
            headers: authHeaders(),
            postData: {},
        });
    } catch (e) {
        // The local session is cleared either way — a failed server-side logout
        // must not strand the user in a signed-in-looking state.
    }
    clearCpcGovSession();
};

/* -----------------------------------------------------------------------
 * Reviewer profile (API-08)
 * --------------------------------------------------------------------- */

/**
 * National Overview for the signed-in country (API-01).
 *
 * Returns the national status counts + tier, and the 6 stakeholder and 18 target
 * rows already labelled. No country argument — the server reads it from the
 * session. There is no numeric score in the response by design.
 */
export const getCpcGovOverviewService = () => {
    const requestObj = {
        path: '/cpc_gov/overview/profile',
        method: 'POST',
        headers: authHeaders(),
        postData: {},
    };
    return invokeApi(requestObj);
};

/**
 * Checklist Explorer rows for the signed-in country (API-02).
 *
 * All filters are optional; pass {} for the full list. There is deliberately no
 * country argument — the server reads it from the session, and a country sent in
 * the body is ignored.
 *
 * @param {{stakeholder_id?:string, target_id?:string, status?:string, search?:string}} filters
 */
export const getCpcGovIndicatorsService = (filters = {}) => {
    const requestObj = {
        path: '/cpc_gov/indicators/list',
        method: 'POST',
        headers: authHeaders(),
        postData: filters,
    };
    return invokeApi(requestObj);
};

/**
 * One indicator and its assessment items, for the signed-in country (API-03).
 *
 * Each item arrives with a `display_case` already decided by the server —
 * scored_ai | scored_external | estimated | no_value — so the screen switches on
 * one field instead of re-deriving the branching from raw provenance codes.
 *
 * `item_level_available` is FALSE for the 15 Child indicators, which carry no
 * item-level detail by design. Say so on screen; do not render an empty list.
 *
 * @param {string} indicator_id canonical id, e.g. "CPCI Indicator 017"
 */
export const getCpcGovIndicatorDetailService = (indicator_id) => {
    const requestObj = {
        path: '/cpc_gov/indicators/detail',
        method: 'POST',
        headers: authHeaders(),
        postData: { indicator_id },
    };
    return invokeApi(requestObj);
};

/**
 * Review progress for the signed-in country (API-10).
 *
 * Returns the readiness stage, item counts and a per-indicator rollup. Takes no
 * arguments — it is a summary of everything, scoped to the session country.
 *
 * `submission_available` is false until API-07 exists: the last stage ("Ready
 * for CPCI Validation") needs a submission, and Seong Min has said that stage is
 * not being built yet. Grey it out rather than implying the country is stuck.
 */
export const getCpcGovProgressService = () => {
    const requestObj = {
        path: '/cpc_gov/progress/summary',
        method: 'POST',
        headers: authHeaders(),
        postData: {},
    };
    return invokeApi(requestObj);
};

/**
 * Attach a PDF or DOCX to the proposal for one item (API-06).
 *
 * multipart/form-data, file under key `media` — the same key every other upload
 * endpoint in this codebase uses.
 *
 * `Content-Type: undefined` is deliberate and load-bearing. invokeApi sets a
 * global axios default of application/json for POST; leaving that in place would
 * send the body without a multipart boundary and the server would parse no file
 * at all. Setting it to undefined makes axios drop the header so the browser
 * writes its own `multipart/form-data; boundary=...`.
 *
 * Left un-encrypted on purpose: invokeApi's e2ee path JSON.stringifies the
 * payload, which would silently reduce a FormData to "{}".
 *
 * PDF and DOCX only, 50 MB max (Seong Min, 11:29 AM). Enforced server-side; the
 * client check below only gives a faster message.
 */
export const uploadCpcGovEvidenceService = (indicator_id, item_id, file) => {
    const formData = new FormData();
    formData.append('media', file);
    formData.append('indicator_id', indicator_id);
    formData.append('item_id', item_id);

    const requestObj = {
        path: '/cpc_gov/review/evidence/upload',
        method: 'POST',
        headers: {...authHeaders(), 'Content-Type': undefined},
        postData: formData,
    };
    return invokeApi(requestObj);
};

/** Accepted evidence formats and size cap — mirrored from the server. */
export const EVIDENCE_MAX_BYTES = 50 * 1024 * 1024;
export const EVIDENCE_EXTENSIONS = ['pdf', 'docx'];

/**
 * Why this file cannot be uploaded, or null when it can.
 *
 * A courtesy check so a 60 MB file is refused instantly instead of after a long
 * upload. The server repeats every one of these — this is not the enforcement.
 */
export const rejectEvidenceFile = (file) => {
    if (!file) return 'no file selected';
    const parts = String(file.name || '').split('.');
    const ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
    if (!EVIDENCE_EXTENSIONS.includes(ext)) {
        return `${ext ? `.${ext} files` : 'this file type'} cannot be accepted — upload a PDF or DOCX, `
            + 'or provide a link in the Source URL field instead';
    }
    if (file.size > EVIDENCE_MAX_BYTES) {
        return `this file is ${(file.size / (1024 * 1024)).toFixed(1)} MB — the limit is 50 MB`;
    }
    if (!file.size) return 'this file is empty';
    return null;
};

/**
 * Ask DQ to run the Children's Assessment for the signed-in country (API-09).
 *
 * ONE request per COUNTRY — all 15 Child indicators are scored from a single
 * survey of children, so the button on any of those rows raises the same
 * request. No indicator is sent.
 *
 * Asking twice is not an error: if a request is already open the server returns
 * it with `already_requested: true`, so the screen shows its status rather than
 * a failure.
 *
 * @param {string} note optional free text from the reviewer
 */
export const requestChildrensAssessmentService = (note = '') => {
    const requestObj = {
        path: '/cpc_gov/request/childrens-assessment',
        method: 'POST',
        headers: authHeaders(),
        postData: {note},
    };
    return invokeApi(requestObj);
};

/**
 * The signed-in country's Children's Assessment request, or null.
 *
 * `can_request` is false once a request exists, so the country cannot ask twice.
 * There is no decision step yet — that flow is still being designed — so nothing
 * currently reopens it.
 */
export const getChildrensAssessmentRequestService = () => {
    const requestObj = {
        path: '/cpc_gov/request/childrens-assessment/status',
        method: 'POST',
        headers: authHeaders(),
        postData: {},
    };
    return invokeApi(requestObj);
};

const CPC_GOV_BASE = '/dq_pro/cpc-government-dashboard';

/**
 * Where a reviewer should land straight after signing in.
 *
 *   terms not accepted -> /disclaimer
 *   otherwise          -> straight to the dashboard
 *
 * Accounts are PRE-CREATED — there is no sign-up — so the only thing that must
 * happen before someone reaches their data is accepting the terms, which is a
 * legal gate and is recorded. The User Information form is NOT a gate: nothing
 * on the dashboard depends on it, so blocking entry behind a form the reviewer
 * never asked to fill in is friction for no benefit.
 *
 * The profile is still collected, and still required — but at the point where it
 * actually matters, which is SUBMISSION. A submission has to name the person
 * accountable for it (attestation terms, clause 8), so the Submit for Validation
 * flow is what should demand a complete profile. Reaching the dashboard does not.
 *
 * It stays reachable any time from "Edit details" in the sidebar.
 *
 * Best effort: if the check fails, send them to the terms rather than past them —
 * showing the terms once too often beats letting someone in with no recorded
 * acceptance.
 */
export const landingRouteAfterLogin = async ({fromAbout = false} = {}) => {
    let attested = false;
    try {
        const res = await getCpcGovUserInfoService();
        attested = !!res?.data?.attested;
    } catch (_) {
        // Best effort: treat an unreadable profile as NOT attested, so the terms
        // are shown once too often rather than skipped entirely.
        attested = false;
    }

    if (attested) return `${CPC_GOV_BASE}/overview/executive-summary`;

   
    return fromAbout ? `${CPC_GOV_BASE}/disclaimer` : CPC_GOV_BASE;
};

/**
 * Record acceptance of the User Attestation and Submission Terms.
 *
 * Sends only `agreed: true` — the server stamps WHICH version of the terms was
 * accepted, so the client cannot claim to have agreed to different wording.
 *
 * Separate from saveCpcGovUserInfoService because the reviewer accepts the terms
 * BEFORE filling the User Information form; folding it into that save would lose
 * the acceptance of anyone who agrees and then stops.
 */
export const saveCpcGovAttestationService = () => {
    const requestObj = {
        path: '/cpc_gov/user/attestation',
        method: 'POST',
        headers: authHeaders(),
        postData: { agreed: true },
    };
    return invokeApi(requestObj);
};

/**
 * The signed-in reviewer's profile plus the country their account is fixed to.
 * Takes no arguments on purpose — the session decides whose profile this is.
 */
export const getCpcGovUserInfoService = () => {
    const requestObj = {
        path: '/cpc_gov/user/info',
        method: 'GET',
        headers: authHeaders(),
    };
    return invokeApi(requestObj);
};

/**
 * Save the User Information screen.
 *
 * Do not add country_code to `form` — the server writes it from the session and
 * ignores anything the client sends.
 */
export const saveCpcGovUserInfoService = (form) => {
    const requestObj = {
        path: '/cpc_gov/user/info',
        method: 'POST',
        headers: authHeaders(),
        postData: form,
    };
    return invokeApi(requestObj);
};

/* -----------------------------------------------------------------------
 * Review workflow (API-04, API-05)
 *
 * None of these take a country. The server writes it from the session and
 * ignores anything sent in the body.
 * --------------------------------------------------------------------- */

/**
 * Record Agree — or an intent to propose — on one assessment item (API-04).
 *
 * @param {string} indicator_id canonical or external id
 * @param {string} item_id
 * @param {'agree'|'propose'} decision
 */
export const saveCpcGovReviewDecisionService = (indicator_id, item_id, decision) => {
    const requestObj = {
        path: '/cpc_gov/review/decision',
        method: 'POST',
        headers: authHeaders(),
        postData: { indicator_id, item_id, decision },
    };
    return invokeApi(requestObj);
};

/**
 * Save a proposed revision for one item (API-05).
 *
 * Upsert — the reviewer is editing one form, and the server keeps the history in
 * the document's change_log.
 *
 * An INCOMPLETE draft is accepted on purpose: a reviewer must be able to save and
 * come back. The response carries `is_complete` and a `missing` list of field
 * names, so the screen can say what is still outstanding rather than silently
 * refusing.
 *
 * Do not send evidence files here — the server discards them. Uploads are API-06.
 */
export const saveCpcGovRevisionProposalService = (proposal) => {
    const requestObj = {
        path: '/cpc_gov/review/proposal',
        method: 'POST',
        headers: authHeaders(),
        postData: proposal,
    };
    return invokeApi(requestObj);
};

/**
 * Every decision and proposal this country has recorded, as
 * reviews[indicator_id][item_id]. One call — a screen never fetches per item.
 *
 * @param {string} [indicator_id] narrows it to a single indicator
 */
export const getCpcGovReviewStateService = (indicator_id) => {
    const requestObj = {
        path: '/cpc_gov/review/state',
        method: 'POST',
        headers: authHeaders(),
        postData: indicator_id ? { indicator_id } : {},
    };
    return invokeApi(requestObj);
};
